import { Box, Button, Flex, Grid, Heading, Image, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { LuBot, LuEye, LuFilm, LuLayoutGrid } from 'react-icons/lu'
import { sectionStateFor, useEventWorkspace } from '../contexts/EventWorkspaceContext'
import { findSection, isSectionVisible } from '../components/eventSettings/sections'
import EventQR from '../components/EventQr'
import { fetchEventContentCounts } from '../services/firebaseService'
import { fetchVideoAIConfig } from '../services/videoAI'
import { useTranslation } from '../i18n'

// The event's images, in the order the images tab shows them.
const IMAGE_FIELDS = [
  { field: 'backgroundUrl', labelKey: 'events.challengeBackground' },
  { field: 'logoUrl', labelKey: 'events.logo' },
  { field: 'prizeBackgroundUrl', labelKey: 'events.prizeBackground' },
  { field: 'splashUrl', labelKey: 'events.splashScreen' },
]

// A badge's colour says what kind of fact it is, from the role palettes in
// src/theme.js — never which badge it is. Each still carries an icon and words.
//   blue    what the event holds (videos, reels)
//   green   a feature switched on
//   yellow  something that needs attention (reception closed)
//   neutral a setting's current value
const TONE = { content: 'blue', on: 'green', attention: 'yellow', value: 'neutral' }

function OverviewBadge({ to, icon: Icon, tone, children }) {
  return (
    <Flex
      as={RouterLink}
      to={to}
      colorPalette={TONE[tone]}
      align="center"
      gap={2}
      px={3}
      py={1.5}
      border="1px solid"
      borderColor="colorPalette.muted"
      borderRadius="full"
      bg="colorPalette.subtle"
      color="colorPalette.fg"
      fontSize="sm"
      fontWeight="600"
      _hover={{ bg: 'colorPalette.muted', borderColor: 'colorPalette.emphasized' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'colorPalette.focusRing', outlineOffset: '2px' }}
      transition="background-color 0.15s ease, border-color 0.15s ease"
    >
      {Icon && <Box as={Icon} boxSize="15px" flexShrink={0} aria-hidden="true" />}
      {children}
    </Flex>
  )
}

const plural = (t, n, one, many) => t(n === 1 ? one : many, { count: n })

/**
 * One badge per thing worth knowing about the event, each a link to where it
 * is changed. Settings that are off get no badge; a tab this person cannot
 * see gets no badge either.
 */
function overviewBadges({ event, counts, ai, ws, s, base, t }) {
  const tab = (id) => {
    const section = findSection(id)
    return section && isSectionVisible(section, s) ? section : null
  }
  const badges = []

  // First, because it is the one that can mean the event is not working.
  const reception = tab('reception')
  if (reception && event.receptionEnabled === false) {
    badges.push({ key: 'reception', tone: 'attention', section: reception, text: t('workspace.badgeReceptionClosed') })
  }

  if (ws.canSeeVideos) {
    if (counts?.videos != null) {
      badges.push({ key: 'videos', tone: 'content', to: `${base}/videos`, icon: LuLayoutGrid, text: plural(t, counts.videos, 'events.videoCountOne', 'events.videoCountMany') })
    }
    if (counts?.reels != null) {
      badges.push({ key: 'reels', tone: 'content', to: `${base}/reels`, icon: LuFilm, text: plural(t, counts.reels, 'events.reelCountOne', 'events.reelCountMany') })
    }
  }

  const moderation = tab('moderation')
  if (moderation && event.defaultModerated) {
    badges.push({ key: 'moderation', tone: 'on', section: moderation, text: t('workspace.badgeAutoApprove') })
  }
  const aiTab = tab('ai')
  if (aiTab && ai?.enabled && ai?.aiModeration) {
    badges.push({ key: 'ai', tone: 'on', section: aiTab, icon: LuBot, text: t('workspace.badgeAiModeration') })
  }

  const challenges = tab('challenges')
  if (challenges) {
    badges.push({ key: 'challenges', tone: 'value', section: challenges, text: plural(t, event.challenges?.length ?? 0, 'workspace.badgeChallengesOne', 'workspace.badgeChallengesMany') })
  }

  const prize = tab('prize')
  if (prize && event.challengeMode) {
    badges.push({ key: 'prize', tone: 'on', section: prize, text: t('workspace.badgePrize') })
  }

  const recording = tab('recording')
  if (recording) {
    badges.push({
      key: 'recording',
      tone: 'value',
      section: recording,
      text: t('workspace.badgeRecording', { clip: event.maxSegmentDuration ?? 5, max: event.maxRecordingDuration ?? 15 }),
    })
  }

  const streaming = tab('streaming')
  if (streaming && event.streamEnabled) {
    badges.push({
      key: 'streaming',
      tone: 'on',
      section: streaming,
      text: event.galleryPublic ? t('workspace.badgeStreamingPublic') : t('workspace.badgeStreaming'),
    })
  }

  return badges.map((b) => (b.section
    ? { ...b, to: `${base}/settings/${b.section.id}`, icon: b.icon ?? b.section.icon }
    : b))
}

/** `/events/:eventId` — what this event holds and is set to, each a link to where to change it. */
export default function EventOverviewPage() {
  const ws = useEventWorkspace()
  const { t } = useTranslation()
  const s = sectionStateFor(ws, null)
  const base = `/events/${ws.eventId}`
  const event = ws.event

  const [counts, setCounts] = useState(null)
  const [ai, setAi] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchEventContentCounts(ws.eventId)
      .then((c) => { if (!cancelled) setCounts(c) })
      .catch(() => {})
    fetchVideoAIConfig(ws.eventId)
      .then((c) => { if (!cancelled) setAi(c) })
      .catch(() => {}) // unreadable means no badge, never an error on the overview
    return () => { cancelled = true }
  }, [ws.eventId])

  const badges = overviewBadges({ event, counts, ai, ws, s, base, t })

  const imagesTab = findSection('images')
  const images = isSectionVisible(imagesTab, s)
    ? IMAGE_FIELDS.filter((img) => event[img.field])
    : []

  return (
    <Stack gap={{ base: 6, lg: 8 }}>
      <Flex gap={4} align="flex-start" justify="space-between" wrap="wrap">
        {/* On a phone the section picker above already names the page. */}
        <Box maxW="65ch" srOnly={{ base: true, lg: false }}>
          <Heading as="h1" size="xl" lineHeight="1.2">{t('workspace.overview')}</Heading>
          <Text mt={1} color="fg.muted">{t('workspace.overviewIntro')}</Text>
        </Box>
        {/* The one thing most people open an event to do. */}
        <Button asChild size="sm" colorPalette="brand">
          <RouterLink to={`${base}/moderation`}><LuEye /> {t('workspace.openModeration')}</RouterLink>
        </Button>
      </Flex>

      <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 280px' }} gap={8} alignItems="start">
        <Stack gap={8}>
          <Box as="section" aria-labelledby="glance-heading">
            <Heading id="glance-heading" as="h2" size="md" mb={3}>{t('workspace.glanceTitle')}</Heading>
            <Flex wrap="wrap" gap={2}>
              {badges.map((b) => (
                <OverviewBadge key={b.key} to={b.to} icon={b.icon} tone={b.tone}>{b.text}</OverviewBadge>
              ))}
            </Flex>
          </Box>

          {images.length > 0 && (
            <Box as="section" aria-labelledby="images-heading">
              <Heading id="images-heading" as="h2" size="md" mb={3}>{t(imagesTab.labelKey)}</Heading>
              <Flex wrap="wrap" gap={3}>
                {images.map((img) => (
                  <Stack
                    key={img.field}
                    as={RouterLink}
                    to={`${base}/settings/images`}
                    gap={1.5}
                    w="140px"
                    p={2}
                    border="1px solid"
                    borderColor="var(--mc-border)"
                    borderRadius="md"
                    bg="var(--mc-bg-surface)"
                    _hover={{ bg: 'var(--mc-bg-raised)', borderColor: 'var(--mc-border-strong)' }}
                    transition="background-color 0.15s ease, border-color 0.15s ease"
                  >
                    <Image
                      src={event[img.field]}
                      alt={t(img.labelKey)}
                      h="96px"
                      w="100%"
                      objectFit="contain"
                      borderRadius="sm"
                      bg="var(--mc-bg-raised)"
                      loading="lazy"
                    />
                    <Text fontSize="xs" color="var(--mc-text-muted)" truncate>{t(img.labelKey)}</Text>
                  </Stack>
                ))}
              </Flex>
            </Box>
          )}
        </Stack>

        <Box as="section" aria-labelledby="share-heading">
          <Heading id="share-heading" as="h2" size="md" mb={3}>{t('workspace.tabShare')}</Heading>
          <EventQR eventId={ws.eventId} webappUrl={ws.webappUrl} hideTitle />
        </Box>
      </Grid>
    </Stack>
  )
}
